import { formatDate } from "../utils/dateUtils";
import { systemFieldsForTab, camposDelPasoAnterior } from "./searchFields";

/**
 * Columnas de la grilla de resultados, indexadas por nombre de campo del sistema.
 *
 * Son el espejo de los campos filtrables: filtrar por un dato que después no se puede ver
 * obliga a abrir los documentos de a uno, y mostrar un dato por el que no se puede filtrar
 * deja al usuario sin forma de acotar la lista.
 *
 * Dos consumidores, con reglas distintas:
 *
 * - **Árbol documental** (`DocTree`): `OPTIONAL_COLUMNS`, la lista de siempre. Sus columnas
 *   fijas —archivo y tamaño— las define `Buscador`, y el resto arranca apagado.
 * - **Control de Calidad** (`ApprovalPage`): `columnsForTab()`, porque ahí lo que tiene
 *   sentido mostrar depende del estado en el que está el documento. Es la misma regla que ya
 *   gobierna los filtros y el orden desde #154, aplicada al tercer consumidor.
 */
const fecha = (valor) => (valor ? formatDate(valor) : '-');
const texto = (valor) => valor || '-';

const CATALOGO_COLUMNAS = {
  // Identificación
  filename: { key: 'filename', label: 'Archivo', render: (doc) => texto(doc.filename) },
  size: {
    key: 'size',
    label: 'Tamaño (KB)',
    align: 'center',
    render: (doc) => (doc.size != null ? Math.round(doc.size / 1024) : '-'),
  },
  // Importación
  createdBy: { key: 'createdBy', label: 'Importado por', render: (doc) => texto(doc.createdBy) },
  createdAt: { key: 'createdAt', label: 'Fecha de importación', render: (doc) => fecha(doc.createdAt) },
  // Revisión
  reviewedBy: { key: 'reviewedBy', label: 'Revisado por', render: (doc) => texto(doc.reviewedBy) },
  reviewedAt: { key: 'reviewedAt', label: 'Fecha de revisión', render: (doc) => fecha(doc.reviewedAt) },
  // Aprobación
  approvedBy: { key: 'approvedBy', label: 'Aprobado por', render: (doc) => texto(doc.approvedBy) },
  approvedAt: { key: 'approvedAt', label: 'Fecha de aprobación', render: (doc) => fecha(doc.approvedAt) },
  // Firma. "Firmado" se deriva de la fecha, igual que del lado del servidor: no hay ningún
  // booleano persistido que pueda quedar desincronizado.
  signed: { key: 'signed', label: 'Firmado', align: 'center', render: (doc) => (doc.signedAt ? 'Sí' : 'No') },
  signedBy: { key: 'signedBy', label: 'Firmado por', render: (doc) => texto(doc.signedBy) },
  signedAt: { key: 'signedAt', label: 'Fecha de firma', render: (doc) => fecha(doc.signedAt) },
  // Rechazo
  rejectedBy: { key: 'rejectedBy', label: 'Rechazado por', render: (doc) => texto(doc.rejectedBy) },
  rejectedAt: { key: 'rejectedAt', label: 'Fecha de rechazo', render: (doc) => fecha(doc.rejectedAt) },
};

/**
 * El motivo de rechazo vive aparte del catálogo porque **no es un campo del sistema**: no está
 * en el índice, así que no se puede filtrar ni ordenar por él. Es la única columna que rompe
 * la simetría, y por eso es la única que se agrega a mano.
 */
const MOTIVO_RECHAZO = {
  key: 'rejectedReason',
  label: 'Motivo rechazo',
  render: (doc) => texto(doc.rejectedReason),
};

/**
 * Las que el usuario puede mostrar u ocultar en el árbol documental. Quedan afuera `filename`
 * y `size` porque ya son columnas fijas de la grilla.
 *
 * Arrancan todas apagadas y la elección se recuerda por usuario — ver `ColumnSelector`.
 */
export const OPTIONAL_COLUMNS = [
  'createdBy', 'createdAt',
  'reviewedBy', 'reviewedAt',
  'approvedBy', 'approvedAt',
  'signed', 'signedBy', 'signedAt',
].map((name) => ({ ...CATALOGO_COLUMNAS[name] }));

/**
 * El criterio de las columnas visibles por defecto es mostrar **el dato del paso que acaba de
 * ocurrir**, que es lo que el usuario está mirando. Antes las cinco pestañas mostraban la fecha
 * de importación, que es el dato más viejo del documento y el menos relevante en cuatro de las
 * cinco. Cuál es ese paso lo decide `camposDelPasoAnterior()` en `searchFields.js`, porque
 * depende de la configuración de la serie.
 *
 * El estado no está en ninguna: cada pestaña ya fuerza el suyo del lado del servidor, así que
 * la columna repetía en todas las filas algo que el usuario sabe por estar parado ahí — y lo
 * escribía en vocabulario interno (`REJECTED`, `SIGNATURE_PENDING`). Lo único que sí importa de
 * ese campo, que a un documento le falten metadatos obligatorios, se muestra como una marca de
 * advertencia sobre el nombre del archivo, y sólo en la pestaña de revisión, que es la única
 * donde se puede resolver (`ApprovalPage`).
 */
/** Cuando no hubo paso anterior con dato propio: los campos que todo documento tiene. */
const FIJAS_POR_DEFECTO = ['filename', 'size', 'createdAt'];

/**
 * Columnas que corresponden a una pestaña de Control de Calidad.
 *
 * El universo sale de `systemFieldsForTab()` y las visibles por defecto de
 * `camposDelPasoAnterior()` — los mismos que alimentan los filtros y el combo de orden—, así
 * que una pestaña no puede terminar mostrando una columna por la que no se puede filtrar ni
 * una que esté vacía en todas las filas. Derivarlo en vez de escribirlo dos veces es lo que
 * evita que vuelvan a divergir.
 *
 * @param tab              `ocr` · `review` · `approval` · `signature` · `rejected`
 * @param approvalRequired si la serie exige aprobación
 * @param reviewRequired   si la serie exige revisión. Sin ella el documento nunca pasa por
 *                         revisión y `reviewed_by` / `reviewed_at` quedan NULL: la pestaña cae
 *                         a los campos de la importación en vez de mostrar dos columnas vacías.
 * @returns `{ fixed, optional }` — las visibles por defecto y las que ofrece el selector
 */
export const columnsForTab = (tab, { approvalRequired = false, reviewRequired = true } = {}) => {
  const opciones = { approvalRequired, reviewRequired };
  const universo = systemFieldsForTab(tab, opciones).map((f) => f.name);

  const pasoAnterior = camposDelPasoAnterior(tab, opciones);
  const fijas = pasoAnterior.length > 0 ? ['filename', ...pasoAnterior] : FIJAS_POR_DEFECTO;

  // Se puede ordenar por cualquier campo del sistema salvo `signed`, que es un booleano
  // virtual resuelto por presencia de `signed_at` — la misma exclusión que hace `Buscador`
  // para el combo de orden.
  //
  // El `filter` no es defensivo de más: el universo se arma en `searchFields.js` y los
  // renderers están acá. Si alguien agrega un campo allá y se olvida del catálogo, sin este
  // filtro saldría una columna sin `key`, sin `label` y sin `render` — una columna fantasma en
  // vez de un error. El test que compara el catálogo contra las cinco pestañas lo detecta en CI.
  const columna = (name) => ({ ...CATALOGO_COLUMNAS[name], sortable: name !== 'signed' });
  const conRenderer = (nombres) => nombres.filter((name) => CATALOGO_COLUMNAS[name]).map(columna);

  const fixed = conRenderer(fijas.filter((name) => universo.includes(name)));
  if (tab === 'rejected') fixed.push({ ...MOTIVO_RECHAZO });

  const optional = conRenderer(universo.filter((name) => !fijas.includes(name)));

  return { fixed, optional };
};

export default OPTIONAL_COLUMNS;
